import { useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Edit, Trash2 } from 'lucide-react';
import { useFolders } from '../hooks/useFolders';

export const AdminFoldersTab = ({ modules, space = 'main' }) => {
  const {
    folders,
    editingFolder,
    folderForm,
    setFolderForm,
    fetchFolders,
    resetFolderForm,
    handleEditFolder,
    saveFolder,
    deleteFolder
  } = useFolders(space);

  // Platinum+ calls these sections; the main platform calls them folders.
  const noun = space === 'platinum_plus' ? 'Section' : 'Folder';
  const low = noun.toLowerCase();
  const parent = space === 'platinum_plus' ? 'Training' : 'Module';

  const folderFormRef = useRef(null);

  useEffect(() => {
    fetchFolders();
  }, []);

  const onEdit = (folder) => {
    handleEditFolder(folder, folderFormRef);
  };

  const onSave = async () => {
    await saveFolder(fetchFolders);
  };

  const onDelete = async (folderId) => {
    await deleteFolder(folderId, fetchFolders);
  };

  return (
    <div className="space-y-6">
      <Card ref={folderFormRef}>
        <CardHeader>
          <CardTitle>Create/Edit {noun}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>{parent} *</Label>
            <Select value={folderForm.module_id} onValueChange={(value) => setFolderForm({ ...folderForm, module_id: value })}>
              <SelectTrigger>
                <SelectValue placeholder={`Select ${parent.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {modules.map((module) => (
                  <SelectItem key={module.id} value={module.id}>{module.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{noun} Name *</Label>
            <Input
              value={folderForm.name}
              onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
              placeholder="e.g., Week 1 - Foundations"
            />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={folderForm.description}
              onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })}
              placeholder={`Brief description of this ${low}`}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Order</Label>
              <Input
                type="number"
                value={folderForm.order}
                onChange={(e) => setFolderForm({ ...folderForm, order: parseInt(e.target.value) })}
                min="1"
              />
            </div>

            <div>
              <Label>Days Delay (Drip-Feed)</Label>
              <Input
                type="number"
                value={folderForm.days_delay}
                onChange={(e) => setFolderForm({ ...folderForm, days_delay: parseInt(e.target.value) })}
                min="0"
                placeholder="0 for immediate access"
              />
              <p className="text-sm text-gray-500 mt-1">
                Days after the previous {low} is completed before this one unlocks
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={onSave}>
              {editingFolder ? `Update ${noun}` : `Create ${noun}`}
            </Button>
            {editingFolder && (
              <Button variant="outline" onClick={resetFolderForm}>
                Cancel Edit
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>All {noun}s (organized by {parent.toLowerCase()})</CardTitle>
        </CardHeader>
        <CardContent>
          {modules.map((module) => {
            const moduleFolders = folders.filter(f => f.module_id === module.id).sort((a, b) => a.order - b.order);
            
            if (moduleFolders.length === 0) return null;

            return (
              <div key={module.id} className="mb-6 border rounded-lg p-4">
                <h3 className="font-bold text-lg mb-3">{module.title}</h3>
                <div className="space-y-2">
                  {moduleFolders.map((folder) => (
                    <div key={folder.id} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{folder.order}. {folder.name}</span>
                          {folder.days_delay > 0 && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                              ⏱️ {folder.days_delay} days delay
                            </span>
                          )}
                        </div>
                        {folder.description && (
                          <p className="text-sm text-gray-600 mt-1">{folder.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEdit(folder)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {noun}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the {low} "{folder.name}" and ALL content items inside it. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => onDelete(folder.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {folders.length === 0 && (
            <p className="text-center text-gray-500 py-8">
              No {low}s created yet. Create your first {low} above!
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminFoldersTab;
